import React, { useEffect, useMemo, useState } from 'react';
import {
  Layout,
  Typography,
  Flex,
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  Tabs,
  Space,
  Popconfirm,
  message,
  Switch,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { useAssistant } from '../../../../contexts/AssistantContext';
import { useAuthGuard } from '../../../../hooks/useAuthGuard';
import { myAssistantServices } from '../../services/myAssistantServices';
import { AppBreadcrumb, useBreadcrumb } from '../../../breadcrumb';
import { createLogger } from '../../../../utils/logger';

const logger = createLogger('features-assistants-tools');
const { Title, Text } = Typography;
const { Content } = Layout;
const { TextArea } = Input;

type ParamType = 'string' | 'integer' | 'number' | 'boolean' | 'array' | 'object';

interface ParamRow {
  key: string;
  name: string;
  type: ParamType;
  description: string;
  enumValues: string;
  required: boolean;
}

interface ToolSchema {
  id?: string;
  name: string;
  type: string;
  function: {
    name: string;
    description: string;
    parameters: {
      type: 'object';
      properties: Record<string, any>;
      required: string[];
    };
  };
}

const emptyTool = (): ToolSchema => ({
  name: '',
  type: 'function',
  function: {
    name: '',
    description: '',
    parameters: { type: 'object', properties: {}, required: [] },
  },
});

const toolToParams = (tool: ToolSchema): ParamRow[] => {
  const props = tool.function.parameters?.properties || {};
  const required = tool.function.parameters?.required || [];
  return Object.entries(props).map(([name, def]: [string, any], i) => ({
    key: `${name}-${i}`,
    name,
    type: (def?.type || 'string') as ParamType,
    description: def?.description || '',
    enumValues: Array.isArray(def?.enum) ? def.enum.join(', ') : '',
    required: required.includes(name),
  }));
};

const paramsToSchema = (rows: ParamRow[]) => {
  const properties: Record<string, any> = {};
  const required: string[] = [];
  rows.forEach((row) => {
    if (!row.name) return;
    const def: any = { type: row.type, description: row.description };
    const enumList = row.enumValues
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    if (enumList.length) def.enum = enumList;
    properties[row.name] = def;
    if (row.required) required.push(row.name);
  });
  return { type: 'object' as const, properties, required };
};

export const MyAssistantTools: React.FC = () => {
  useAuthGuard();
  const { currentAssistant } = useAssistant();
  const chatbotId = currentAssistant?.id || '';

  const [tools, setTools] = useState<ToolSchema[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ToolSchema | null>(null);
  const [activeTab, setActiveTab] = useState<'form' | 'json'>('form');
  const [saving, setSaving] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [params, setParams] = useState<ParamRow[]>([]);

  // JSON state
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const breadcrumbItems = useBreadcrumb({ section: 'Tools', includeAssistant: true });

  const fetchTools = async () => {
    if (!chatbotId) return;
    setLoading(true);
    try {
      const data = await myAssistantServices.listToolSchemas(chatbotId);
      setTools(data);
    } catch (e) {
      logger.error('Error loading tools', e);
      message.error('Failed to load tools');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
  }, [chatbotId]);

  const openCreate = () => {
    const t = emptyTool();
    setEditing(t);
    setFormName('');
    setFormDescription('');
    setParams([]);
    setJsonText(JSON.stringify({ type: t.type, function: t.function }, null, 2));
    setJsonError(null);
    setActiveTab('form');
    setModalOpen(true);
  };

  const openEdit = (tool: ToolSchema) => {
    setEditing(tool);
    setFormName(tool.function?.name || tool.name || '');
    setFormDescription(tool.function?.description || '');
    setParams(toolToParams(tool));
    setJsonText(JSON.stringify({ type: tool.type, function: tool.function }, null, 2));
    setJsonError(null);
    setActiveTab('form');
    setModalOpen(true);
  };

  const openDuplicate = (tool: ToolSchema) => {
    const clone = JSON.parse(JSON.stringify(tool)) as ToolSchema;
    delete clone.id;
    clone.name = `${clone.name}_copy`;
    if (clone.function) clone.function.name = `${clone.function.name}_copy`;
    setEditing(clone);
    setFormName(clone.function?.name || '');
    setFormDescription(clone.function?.description || '');
    setParams(toolToParams(clone));
    setJsonText(JSON.stringify({ type: clone.type, function: clone.function }, null, 2));
    setJsonError(null);
    setActiveTab('form');
    setModalOpen(true);
  };

  const handleDelete = async (tool: ToolSchema) => {
    if (!tool.id) return;
    try {
      await myAssistantServices.deleteToolSchema(tool.id);
      message.success('Tool deleted');
      fetchTools();
    } catch (e) {
      logger.error('Error deleting tool', e);
      message.error('Failed to delete tool');
    }
  };

  // When switching to JSON tab, sync from form
  const syncFormToJson = () => {
    const built = {
      type: 'function',
      function: {
        name: formName,
        description: formDescription,
        parameters: paramsToSchema(params),
      },
    };
    setJsonText(JSON.stringify(built, null, 2));
    setJsonError(null);
  };

  // When switching to Form tab, parse from JSON
  const syncJsonToForm = (): boolean => {
    try {
      const parsed = JSON.parse(jsonText);
      const fn = parsed.function || {};
      setFormName(fn.name || '');
      setFormDescription(fn.description || '');
      setParams(
        toolToParams({
          name: fn.name || '',
          type: parsed.type || 'function',
          function: {
            name: fn.name || '',
            description: fn.description || '',
            parameters: fn.parameters || { type: 'object', properties: {}, required: [] },
          },
        }),
      );
      setJsonError(null);
      return true;
    } catch (e: any) {
      setJsonError(e.message || 'Invalid JSON');
      return false;
    }
  };

  const handleTabChange = (key: string) => {
    if (key === 'json' && activeTab === 'form') {
      syncFormToJson();
    } else if (key === 'form' && activeTab === 'json') {
      if (!syncJsonToForm()) return;
    }
    setActiveTab(key as 'form' | 'json');
  };

  const handleSave = async () => {
    if (!chatbotId) return;
    let payload: any;
    if (activeTab === 'json') {
      try {
        const parsed = JSON.parse(jsonText);
        payload = {
          chatbotId,
          name: parsed.function?.name || formName,
          type: parsed.type || 'function',
          function: parsed.function,
        };
      } catch (e: any) {
        setJsonError(e.message || 'Invalid JSON');
        return;
      }
    } else {
      if (!formName.trim()) {
        message.error('Name is required');
        return;
      }
      const droppedUnnamed = params.filter((p: ParamRow) => !p.name.trim()).length;
      if (droppedUnnamed > 0) {
        message.warning(
          `${droppedUnnamed} parameter${droppedUnnamed === 1 ? '' : 's'} without a name will not be saved`,
        );
      }
      payload = {
        chatbotId,
        name: formName.trim(),
        type: 'function',
        function: {
          name: formName.trim(),
          description: formDescription,
          parameters: paramsToSchema(params),
        },
      };
    }

    setSaving(true);
    try {
      if (editing?.id) {
        await myAssistantServices.updateToolSchema(editing.id, payload);
        message.success('Tool updated');
      } else {
        await myAssistantServices.createToolSchema(payload);
        message.success('Tool created');
      }
      setModalOpen(false);
      fetchTools();
    } catch (e) {
      logger.error('Error saving tool', e);
      message.error('Failed to save tool');
    } finally {
      setSaving(false);
    }
  };

  const addParam = () => {
    setParams((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}`,
        name: '',
        type: 'string',
        description: '',
        enumValues: '',
        required: false,
      },
    ]);
  };

  const updateParam = (key: string, patch: Partial<ParamRow>) => {
    setParams((prev) => prev.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  };

  const removeParam = (key: string) => {
    setParams((prev) => prev.filter((p) => p.key !== key));
  };

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: ['function', 'name'],
        key: 'name',
        render: (_: any, record: ToolSchema) => (
          <Space>
            <Text strong>{record.function?.name || record.name}</Text>
            <Tag color="blue">{record.type}</Tag>
          </Space>
        ),
      },
      {
        title: 'Description',
        dataIndex: ['function', 'description'],
        key: 'description',
        render: (text: string) => (
          <Text type="secondary" ellipsis={{ tooltip: text }} style={{ maxWidth: 480 }}>
            {text}
          </Text>
        ),
      },
      {
        title: 'Parameters',
        key: 'params',
        render: (_: any, record: ToolSchema) => {
          const count = Object.keys(record.function?.parameters?.properties || {}).length;
          return <Tag>{count}</Tag>;
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 200,
        render: (_: any, record: ToolSchema) => (
          <Space>
            <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
            <Button icon={<CopyOutlined />} size="small" onClick={() => openDuplicate(record)} />
            <Popconfirm
              title="Delete this tool?"
              onConfirm={() => handleDelete(record)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button icon={<DeleteOutlined />} size="small" danger disabled={!record.id} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <AppBreadcrumb items={breadcrumbItems} style={{ flexShrink: 0, marginBottom: '10px' }} />
      <Card>
        <Content>
          <Flex vertical>
            <Flex justify="space-between" align="center" style={{ marginBottom: '24px' }}>
              <div>
                <Title level={2} style={{ margin: 0 }}>
                  Tools
                </Title>
                <Text type="secondary">
                  Define the functions the agent can invoke
                </Text>
              </div>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                New tool
              </Button>
            </Flex>

            <Table
              rowKey={(record) => record.id || record.function?.name || record.name}
              dataSource={tools}
              columns={columns as any}
              loading={loading}
              pagination={{ pageSize: 20 }}
            />
          </Flex>
        </Content>
      </Card>

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        title={editing?.id ? 'Edit tool' : 'New tool'}
        width={900}
        okText="Save"
        cancelText="Cancel"
        destroyOnClose
      >
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: 'form',
              label: 'Form',
              children: (
                <Form layout="vertical">
                  <Form.Item label="Function name" required>
                    <Input
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="consultar_viajes"
                    />
                  </Form.Item>
                  <Form.Item label="Description">
                    <TextArea
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      rows={4}
                      placeholder="What the function does, when to use it, what it returns…"
                    />
                  </Form.Item>

                  <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                    <Text strong>Parameters</Text>
                    <Button size="small" icon={<PlusOutlined />} onClick={addParam}>
                      Add parameter
                    </Button>
                  </Flex>

                  {params.length === 0 ? (
                    <Text type="secondary">No parameters.</Text>
                  ) : (
                    <Flex vertical gap={12}>
                      {params.map((p) => (
                        <Card key={p.key} size="small">
                          <Flex gap={8} wrap="wrap" align="flex-start">
                            <Input
                              placeholder="name"
                              value={p.name}
                              onChange={(e) => updateParam(p.key, { name: e.target.value })}
                              style={{ width: 180 }}
                            />
                            <Select
                              value={p.type}
                              onChange={(v) => updateParam(p.key, { type: v })}
                              style={{ width: 130 }}
                              options={[
                                { value: 'string', label: 'string' },
                                { value: 'integer', label: 'integer' },
                                { value: 'number', label: 'number' },
                                { value: 'boolean', label: 'boolean' },
                                { value: 'array', label: 'array' },
                                { value: 'object', label: 'object' },
                              ]}
                            />
                            <Input
                              placeholder="description"
                              value={p.description}
                              onChange={(e) =>
                                updateParam(p.key, { description: e.target.value })
                              }
                              style={{ flex: 1, minWidth: 200 }}
                            />
                            <Input
                              placeholder="enum (comma-separated, optional)"
                              value={p.enumValues}
                              onChange={(e) =>
                                updateParam(p.key, { enumValues: e.target.value })
                              }
                              style={{ width: 240 }}
                            />
                            <Flex align="center" gap={4}>
                              <Text style={{ fontSize: 12 }}>Req</Text>
                              <Switch
                                size="small"
                                checked={p.required}
                                onChange={(v) => updateParam(p.key, { required: v })}
                              />
                            </Flex>
                            <Button
                              icon={<DeleteOutlined />}
                              danger
                              size="small"
                              onClick={() => removeParam(p.key)}
                            />
                          </Flex>
                        </Card>
                      ))}
                    </Flex>
                  )}
                </Form>
              ),
            },
            {
              key: 'json',
              label: 'Raw JSON',
              children: (
                <Flex vertical gap={8}>
                  <Text type="secondary">
                    Paste the object with <Text code>type</Text> and <Text code>function</Text>{' '}
                    (same shape as the curl body, without <Text code>chatbotId</Text>).
                  </Text>
                  <TextArea
                    value={jsonText}
                    onChange={(e) => {
                      setJsonText(e.target.value);
                      setJsonError(null);
                    }}
                    rows={20}
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                  {jsonError && <Text type="danger">{jsonError}</Text>}
                </Flex>
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
};

export default MyAssistantTools;
